import React, { FC, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ScreenProps } from "../types/appTypes";
import LoginForm from "./forms/login/loginForm";
import { getLoginMode, getPath } from "../routes/appRoutes";
import ForgotPassword from "./forms/login/forgotPassword";
import ResetPassword from "./forms/login/resetPassword";
import { getCurrentUser } from "../services/authService";
import { ParamLoginTypes } from "../types/loginTypes";
import { ReactComponent as Logo } from "../assets/images/dmf_logo.svg";
import { ReactComponent as LogoTagLine } from "../assets/images/dmf_tagLine.svg";
import { Badge, Button, Checkbox, Col, Divider, Dropdown, Modal, Radio, Row, Tag } from "antd";
import appConfig from "../appConfig";
import ContactMenuDropdown from "../components/contactMenu";
import UserStore from "../contexts/userStore";
import LayoutStore from "../contexts/layoutStore";
import _t from "../lang/translate";
import { useLocalStorage } from "../hooks/useLocalStorage";
import DividerLine from "../components/lineSplit";
import Icon from "../assets/icons/icon";
import { isEmbedded } from "../utilities/isEmbedded";

const DMF_PRO_ORIGIN = process.env.REACT_APP_DMF_PRO_ORIGIN
const DMF_PRO_LOGIN_PATH = process.env.REACT_APP_DMF_PRO_LOGIN_PATH
const DMF_PRO_LOGIN_URL = (DMF_PRO_ORIGIN ?? "") + (DMF_PRO_LOGIN_PATH ?? "");
const LOGIN_METHOD_STORAGE_KEY = "dmf.login.method";

const Login: FC<ScreenProps> = ({ title }) => {
  const history = useHistory();
  const { screen } = LayoutStore.useContainer();
  const isMobile = screen.isMobile;
  const { showCookiePolicy } = UserStore.useContainer();
  const showCookies = !!process.env.REACT_APP_COOKIEBOT_ID;
  const forceAdmin = history.location.search.includes('forceAdmin')
  const [loginMethodStorage, setLoginMethodStorage] = useLocalStorage<"dmfpro" | "dmfnet" | null>(LOGIN_METHOD_STORAGE_KEY, null)
  const [showPortalModal, setShowPortalModal] = useState(() => {
    if (isEmbedded()) return false;
    if (forceAdmin) return true;
    if (!loginMethodStorage) return true
    return false
  })
  const [rememberChoice, setRememberChoice] = useState(false)

  const { mode } = useParams<ParamLoginTypes>();

  useEffect(() => {
    const user = getCurrentUser();
    if (user !== null) history.push(getPath("home"));
  }, [history]);

  useEffect(() => {
    if (forceAdmin || isEmbedded()) return;

    if (loginMethodStorage === 'dmfpro' && DMF_PRO_LOGIN_URL) {
      window.location.href = DMF_PRO_LOGIN_URL;
    }
    else if (loginMethodStorage === "dmfnet") {
      setShowPortalModal(false)
    }
  }, []);

  const getForm = (currentMode?: string) => {
    if (currentMode === "forgotPassword") {
      return <ForgotPassword />;
    } else if (currentMode === "resetPassword") {
      return <ResetPassword />;
    } else {
      return <LoginForm title={title} />;
    }
  };
  function goToDMFPro() {
    if (rememberChoice) {
      setLoginMethodStorage('dmfpro')
    }
    if (DMF_PRO_LOGIN_URL) {
      window.location.href = DMF_PRO_LOGIN_URL;
    }
  }
  function continueToInternalAdministration() {
    if (rememberChoice) {
      setLoginMethodStorage('dmfnet')
    }
    setShowPortalModal(false)
  }

  return (
    <>
      <Row className="login">
        <Col flex={'500px'} className="transition">

          <div className="login-form-wp">
            <div className="login-mobile-logo">
              <Logo />
            </div>
            <div>{getForm(getLoginMode(mode))}</div>
            <div className="login-footer">
              <Row gutter={16} justify={isMobile ? "start" : "space-between"} align="middle">
                <Col>
                  <span className="muted">{appConfig("companyName")}</span>
                </Col>
                <Col>
                  <ContactMenuDropdown />
                </Col>

                {showCookies && (
                  <Col>
                    <Button className="underline-style p-0" type="link" onClick={showCookiePolicy}>
                      {_t("cookie_policy")}
                    </Button>
                  </Col>
                )}
              </Row>
            </div>
          </div>
        </Col>
        <Col flex="auto" className="login-bg">
          <LogoTagLine className="position-relative z-4" />
        </Col>
      </Row>
      <Modal visible={showPortalModal}
        closable={false}
        footer={null}
        width={768} title={"Vælg din portal"}>
        <p>DMF Administration er kun for medarbejdere og interne brugere. Er du bilforhandler eller ekstern samarbejdspartner, skal du logge ind via DMF Pro.</p>
        <Row gutter={[16, 16]}>
          <Col flex={"1 1 50%"} >
            <button onClick={goToDMFPro} className="login-portal-card-btn recommended-action ">
              <Row className="mb-1" justify="space-between">
                <Col className="p-05 border-radius bg-primary">
                  <Icon className="text-white" size="huge" name="buildings" />
                </Col>
                <Col>
                  <Tag className="ant-tag-exception text-upper bg-primary border-primary text-white">For forhandlere</Tag>
                </Col>
              </Row>
              <h3 className="mb-0">DMF Pro</h3>
              <p className="mb-0">For bilforhandlere og eksterne samarbejdspartner</p>
              <div className="login-portal-card-btn-inner-btn">
                {_t("go_to_dmfpro")}
                <Icon className="ml-03" name="arrow-forward-outline" />
              </div>
            </button>
          </Col>
          <Col flex={"1 1 50%"}>
            <button onClick={continueToInternalAdministration} className="login-portal-card-btn p-1 height-100 border-radius border-light bg-white">
              <div className="mb-1 d-inline-block p-05 border-radius bg-light">
                <Icon size="mega" name="dmf" />
              </div>
              <h3 className="mb-0">Intern administration</h3>
              <p className="mb-0">For medarbejdere hos Dansk Motor Finans</p>
              <div className="login-portal-card-btn-inner-btn">
                {_t("continue_to_login")}
                <Icon className="ml-03" name="arrow-forward-outline" />
              </div>
            </button>
          </Col>
        </Row>
        <DividerLine />
        <Checkbox onChange={(e) => setRememberChoice(e.target.checked)} checked={rememberChoice}>Husk mit valg på denne enhed</Checkbox>
      </Modal>
    </>
  )
};

export default Login;
